// ClienteArea
import React, { useState, useEffect, useCallback } from 'react';
import { 
    User, ChevronLeft, Clock, Package, 
    Ticket, LogOut, ChevronRight, 
    MapPin, Smartphone, Mail, Info, Edit3, Save, X,
    Printer, Share2, ReceiptText, CreditCard, Bike,
    Trophy, RotateCw, CheckCircle2, Gift, Loader2,
    Bell, MessageSquare, Star, Sparkles, Zap, FileText, QrCode, Copy, Check, RefreshCw
} from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

// Importação dos estilos originais
import '../styles/Cupom.css';
import './MinhaLoja/styles/Fidelidade.css'; 

export default function ClienteArea({ clienteLogado, pedidosCliente, corPrimaria, setViewMode, setClienteLogado, setPedidosCliente, handleComprarNovamente }) {
    const [tabAtiva, setTabAtiva] = useState('pedidos'); 
    const [editando, setEditando] = useState(false);
    const [formData, setFormData] = useState({ ...clienteLogado });
    const [pedidoSelecionado, setPedidoSelecionado] = useState(null);
    const [loadingDetalhes, setLoadingDetalhes] = useState(false);
    const [copiadoPixId, setCopiadoPixId] = useState(null);
    const [loadingPixId, setLoadingPixId] = useState(null); // NOVO: Controle de loading do botão de gerar Pix

    // --- ESTADOS DA FIDELIDADE E MARKETING ---
    const [fidelidade, setFidelidade] = useState(null);
    const [mensagens, setMensagens] = useState([]);
    const [cuponsExclusivos, setCuponsExclusivos] = useState([]);
    const [flipped, setFlipped] = useState(false);

    // --- FILTRAR PEDIDOS PENDENTES DE PAGAMENTO (PIX) ---
    const pedidosPendentesPagamento = pedidosCliente.filter(p => 
        String(p.status || '').toLowerCase() === 'pendente' || 
        String(p.status_pagamento || '').toLowerCase() === 'pendente' ||
        p.qrcode_pix || p.copia_e_cola
    );

    const copiarPix = (texto, idPedido) => {
        navigator.clipboard.writeText(texto);
        setCopiadoPixId(idPedido);
        toast.success("Chave Pix Copia e Cola copiada!");
        setTimeout(() => setCopiadoPixId(null), 3000);
    };

    // --- FUNÇÃO PARA GERAR O PIX SE NÃO EXISTIR ---
    const handleGerarPix = async (pedido) => {
        setLoadingPixId(pedido.id);
        try {
            const res = await api.post(`/pagamento/gerar-pix-pedido`, {
                pedido_id: pedido.id,
                valor: pedido.total,
                estabelecimento_id: clienteLogado?.estabelecimento_id || pedido.estabelecimento_id
            });

            if (res.data.qrcode_pix || res.data.qrcode_base64) {
                const qrCode = res.data.qrcode_pix || res.data.qrcode_base64;
                const copiaCola = res.data.copia_e_cola || res.data.copia_paste;

                setPedidosCliente(prev => prev.map(p => 
                    p.id === pedido.id ? { ...p, qrcode_pix: qrCode, copia_e_cola: copiaCola } : p
                ));
                toast.success("Pix gerado com sucesso!");
            } else {
                toast.error("Erro ao recuperar dados do Pix.");
            }
        } catch (error) {
            console.error("Erro na geração do PIX:", error);
            toast.error("Ocorreu um erro ao gerar o pagamento.");
        } finally {
            setLoadingPixId(null);
        }
    };

    // --- CARREGAMENTO DE DADOS (PEDIDOS, FIDELIDADE E MARKETING) ---
    const carregarTudo = useCallback(async () => {
        const clienteLocal = clienteLogado || JSON.parse(localStorage.getItem('clienteLogado'));
        
        const cpfLimpo = clienteLocal?.cpf?.replace(/\D/g, '');
        const token = clienteLocal?.token || clienteLocal?.token_acesso || localStorage.getItem('token_acesso');

        if (!cpfLimpo || !token) {
            console.warn("⚠️ Aguardando dados do cliente para carregar pedidos/fidelidade.");
            return;
        }

        try {
            const resGeral = await api.get(`/clientes/pedidos/${cpfLimpo}?token=${token}`);
            
            if (resGeral.data?.success) {
                setPedidosCliente(resGeral.data.pedidos || []);
                setFidelidade(resGeral.data.fidelidade); 
            }

            const resMarketing = await api.get(`/marketing/cliente/${clienteLocal.id}?estabelecimento_id=${clienteLocal.estabelecimento_id}`);
            
            if (resMarketing.data) {
                const cupons = resMarketing.data.cupons || resMarketing.data || [];
                setCuponsExclusivos(cupons);
                setMensagens(resMarketing.data.mensagens || []);
            }
        } catch (error) {
            console.error("❌ Erro ao carregar Área do Cliente:", error);
            if (error.response?.status === 401) {
                toast.error("Sessão expirada. Por favor, faça login novamente.");
            }
        }
    }, [clienteLogado, setPedidosCliente]);

    useEffect(() => {
        if (clienteLogado) {
            setFormData({ ...clienteLogado });
            carregarTudo(); 
        }
    }, [clienteLogado?.id, tabAtiva, carregarTudo]);
    
    const handleUpdatePerfil = async () => {
        try {
            await api.put(`/clientes/atualizar/${clienteLogado.id}`, formData);
            setClienteLogado(formData);
            setEditando(false);
            toast.success("Dados atualizados com sucesso!");
        } catch (error) {
            toast.error("Erro ao atualizar perfil.");
        }
    };

    const abrirDetalhes = async (pedido) => {
        setLoadingDetalhes(true);
        setPedidoSelecionado(pedido); 
        try {
            const res = await api.get(`/clientes/pedido-detalhes/${pedido.id}`);
            if (res.data.success) {
                setPedidoSelecionado({
                    ...res.data.pedido,
                    itens: res.data.itens
                });
            }
        } catch (error) {
            toast.error("Erro ao carregar os itens.");
            console.error(error);
        } finally {
            setLoadingDetalhes(false);
        }
    };
    
    const handleLogout = () => {
        localStorage.removeItem('clienteLogado');
        localStorage.removeItem('token_acesso');
        setClienteLogado(null);
        setViewMode('menu');
        toast.success("Sessão encerrada.");
    };

    const imprimirCupom = () => {
        const conteudoCupom = document.getElementById('cupom-fiscal').innerHTML;
        const janelaImpressao = window.open('', '', 'width=800,height=600');
        janelaImpressao.document.write(`
            <html>
                <head>
                    <title>Impressão - Universo Mídia</title>
                    <style>
                        body { font-family: 'Courier New', monospace; padding: 20px; }
                        .cupom-divider { border-top: 1px dashed #000; margin: 10px 0; }
                        .cupom-header { text-align: center; text-transform: uppercase; }
                        .cupom-item-row { display: flex; justify-content: space-between; margin-bottom: 5px; }
                        .cupom-total-row { display: flex; justify-content: space-between; font-weight: bold; border-top: 1px solid #000; padding-top: 5px; }
                    </style>
                </head>
                <body>
                    ${conteudoCupom}
                    <script>window.onload = function() { window.print(); window.close(); };</script>
                </body>
            </html>
        `);
        janelaImpressao.document.close();
    };

    const compartilharPedido = () => {
        const msg = `Resumo Pedido #${pedidoSelecionado.id}\nStatus: ${pedidoSelecionado.status}\nTotal: R$ ${parseFloat(pedidoSelecionado.total).toFixed(2)}`;
        if (navigator.share) {
            navigator.share({ title: 'Meu Pedido', text: msg, url: window.location.href });
        } else {
            navigator.clipboard.writeText(msg);
            toast.success("Copiado!");
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: '#f8fafc', paddingBottom: '100px' }}>
            
            {/* HEADER SUPERIOR */}
            <header style={{ 
                background: '#fff', 
                padding: '15px 20px', 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                borderBottom: '1px solid #e2e8f0', 
                position: 'sticky', 
                top: 0, 
                zIndex: 100 
            }}>
                <button 
                    onClick={() => setViewMode('menu')} 
                    style={{ background: '#f1f5f9', border: 'none', padding: '8px', borderRadius: '12px', color: '#64748b', cursor: 'pointer' }}
                >
                    <ChevronLeft size={24} />
                </button>
            
                <h3 style={{ margin: 0, fontWeight: '900', fontSize: '16px', color: '#0f172a' }}>MINHA CONTA</h3>
            
                <button 
                    onClick={handleLogout} 
                    style={{ background: '#fff1f2', border: 'none', padding: '8px', borderRadius: '12px', color: '#ef4444', cursor: 'pointer' }}
                >
                    <LogOut size={20} />
                </button>
            </header>

            <main style={{ padding: '20px', maxWidth: '600px', margin: '0 auto', width: '100%' }}>
                
                {/* CARD DE BOAS VINDAS (VIP) */}
                <div style={{ background: `linear-gradient(135deg, ${corPrimaria}, #000)`, borderRadius: '24px', padding: '25px', color: '#fff', marginBottom: '25px', boxShadow: `0 15px 30px ${corPrimaria}30`, display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', fontWeight: '900', border: '2px solid rgba(255,255,255,0.4)' }}>
                        {clienteLogado.nome?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '900' }}>Olá, {clienteLogado.nome.split(' ')[0]}</h2>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', opacity: 0.9, marginTop: '4px' }}>
                            <Star size={14} fill="#fff" /> Membro desde {new Date(clienteLogado.criado_em || Date.now()).getFullYear()}
                        </div>
                    </div>
                </div>

                {/* CENTRAL DE MENSAGENS / NOTIFICAÇÕES */}
                {mensagens.length > 0 && (
                    <div style={{ marginBottom: '25px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', color: '#1e293b' }}>
                            <Bell size={18} color={corPrimaria} />
                            <span style={{ fontWeight: '900', fontSize: '13px' }}>AVISOS E MENSAGENS</span>
                        </div>
                        <div style={{ display: 'flex', overflowX: 'auto', gap: '12px', paddingBottom: '10px' }} className="no-scrollbar">
                            {mensagens.map(msg => (
                                <div key={msg.id} style={{ minWidth: '280px', background: '#fff', padding: '15px', borderRadius: '20px', borderLeft: `6px solid ${corPrimaria}`, boxShadow: '0 4px 10px rgba(0,0,0,0.03)' }}>
                                    <h5 style={{ margin: '0 0 5px', fontWeight: '800', fontSize: '14px' }}>{msg.titulo}</h5>
                                    <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>{msg.texto}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* CONTEÚDO DINÂMICO POR ABAS */}
                <div className="tab-container">
                    
                     {/* ABA PEDIDOS */}
                    {tabAtiva === 'pedidos' && (
                        <div className="pedidos-list">
                            <h4 style={{ fontWeight: '800', marginBottom: '15px', color: '#1e293b' }}>Meus Pedidos</h4>
                            {pedidosCliente.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '40px', background: '#fff', borderRadius: '20px' }}>
                                    <Package size={40} color="#cbd5e1" style={{ marginBottom: '10px' }} />
                                    <p style={{ color: '#64748b' }}>Nenhum pedido encontrado.</p>
                                </div>
                            ) : pedidosCliente.map(ped => (
                                <div key={ped.id} 
                                     onClick={() => abrirDetalhes(ped)}
                                     style={{ background: '#fff', padding: '15px', borderRadius: '15px', marginBottom: '12px', border: '1px solid #f1f5f9', cursor: 'pointer', transition: '0.2s' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div>
                                            <span style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8' }}>#{ped.id}</span>
                                            <p style={{ margin: 0, fontWeight: '800', color: '#1e293b' }}>R$ {parseFloat(ped.total).toFixed(2).replace('.', ',')}</p>
                                        </div>
                                        <div style={{ textAlign: 'right', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <span style={{ 
                                                background: ped.status === 'pago' ? '#dcfce7' : '#fef3c7', 
                                                color: ped.status === 'pago' ? '#16a34a' : '#d97706', 
                                                padding: '3px 10px', borderRadius: '10px', fontSize: '10px', fontWeight: '900' 
                                            }}>
                                                {ped.status.toUpperCase()}
                                            </span>
                                            <ChevronRight size={18} color="#cbd5e1" />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* ABA: PAGAMENTOS PIX */}
                    {tabAtiva === 'pagamentos' && (
                        <div style={{ animation: 'fadeIn 0.3s ease' }}>
                            <h4 style={{ fontWeight: '800', marginBottom: '15px', color: '#1e293b' }}>Pagamentos Pix Pendentes</h4>
                            {pedidosPendentesPagamento.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '40px', background: '#fff', borderRadius: '20px', border: '1px solid #e2e8f0' }}>
                                    <QrCode size={40} color="#cbd5e1" style={{ marginBottom: '10px' }} />
                                    <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>Nenhum pagamento Pix pendente no momento.</p>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                    {pedidosPendentesPagamento.map((pedido) => (
                                        <div key={pedido.id} style={{ background: '#fff', borderRadius: '20px', padding: '20px', border: '1.5px solid #e2e8f0', textAlign: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
                                            <h5 style={{ margin: '0 0 5px 0', fontSize: '16px', color: '#1e293b' }}>Pedido #{pedido.id}</h5>
                                            <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '15px' }}>Valor: <strong>R$ {parseFloat(pedido.total || 0).toFixed(2).replace('.', ',')}</strong></p>

                                            {pedido.qrcode_pix || pedido.copia_e_cola ? (
                                                <>
                                                    {pedido.qrcode_pix && (
                                                        <div style={{ margin: '15px 0' }}>
                                                            <img src={pedido.qrcode_pix} alt="QR Code Pix" style={{ width: '160px', height: '160px', borderRadius: '12px', border: '1px solid #e2e8f0', margin: '0 auto' }} />
                                                        </div>
                                                    )}

                                                    {pedido.copia_e_cola && (
                                                        <div style={{ marginTop: '15px' }}>
                                                            <textarea
                                                                readOnly
                                                                value={pedido.copia_e_cola}
                                                                style={{ width: '100%', height: '60px', padding: '10px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '11px', background: '#f8fafc', resize: 'none' }}
                                                            />
                                                            <button
                                                                onClick={() => copiarPix(pedido.copia_e_cola, pedido.id)}
                                                                style={{ marginTop: '10px', width: '100%', background: corPrimaria, color: '#fff', border: 'none', padding: '12px', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                                                            >
                                                                {copiadoPixId === pedido.id ? <Check size={18} /> : <Copy size={18} />}
                                                                {copiadoPixId === pedido.id ? 'Copiado com sucesso!' : 'Copiar Pix Copia e Cola'}
                                                            </button>
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <div style={{ marginTop: '15px' }}>
                                                    <button
                                                        onClick={() => handleGerarPix(pedido)}
                                                        disabled={loadingPixId === pedido.id}
                                                        style={{ width: '100%', background: corPrimaria, color: '#fff', border: 'none', padding: '12px', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', opacity: loadingPixId === pedido.id ? 0.7 : 1 }}
                                                    >
                                                        {loadingPixId === pedido.id ? (
                                                            <Loader2 size={18} className="spin" />
                                                        ) : (
                                                            <QrCode size={18} />
                                                        )}
                                                        {loadingPixId === pedido.id ? 'Gerando Pix...' : 'Gerar Pagamento Pix'}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ABA: CUPONS E FIDELIDADE (BENEFÍCIOS) */}
                    {tabAtiva === 'cupons' && (
                        <div style={{ animation: 'fadeIn 0.3s ease' }}>
                            
                            {/* 1. SEÇÃO DE PRÊMIOS LIBERADOS */}
                            {fidelidade?.premios_liberados?.length > 0 && (
                                <div style={{ marginBottom: '30px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                        <Gift size={20} color="#16a34a" />
                                        <h4 style={{ fontWeight: '900', color: '#166534', margin: 0, fontSize: '14px' }}>PRÊMIOS DISPONÍVEIS</h4>
                                    </div>
                                    
                                    {fidelidade.premios_liberados.map((premio, idx) => (
                                        <div key={idx} style={{ 
                                            background: '#fff', padding: '16px', borderRadius: '20px', border: '1px solid #dcfce7', 
                                            marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '15px',
                                            boxShadow: '0 4px 15px rgba(22, 163, 74, 0.08)', position: 'relative', overflow: 'hidden'
                                        }}>
                                            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '6px', background: '#16a34a' }}></div>
                                            
                                            <div style={{ background: '#dcfce7', padding: '10px', borderRadius: '12px' }}>
                                                <Trophy color="#16a34a" size={24} />
                                            </div>
                                            
                                            <div style={{ flex: 1 }}>
                                                <span style={{ fontSize: '10px', fontWeight: '800', color: '#16a34a', textTransform: 'uppercase' }}>Parabéns! Você completou um cartão</span>
                                                <strong style={{ color: '#0f172a', fontSize: '15px', display: 'block' }}>{fidelidade.config?.premio || 'Prêmio Fidelidade'}</strong>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '4px' }}>
                                                    <span style={{ fontSize: '12px', color: '#64748b' }}>Código:</span>
                                                    <code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: '6px', fontSize: '13px', fontWeight: '900', color: '#1e293b' }}>
                                                        {premio.codigo_resgate}
                                                    </code>
                                                </div>
                                            </div>
    
                                            <button 
                                                onClick={() => { 
                                                    navigator.clipboard.writeText(premio.codigo_resgate); 
                                                    toast.success("Código do prêmio copiado!"); 
                                                }}
                                                style={{ background: '#16a34a', color: '#fff', border: 'none', padding: '10px 14px', borderRadius: '12px', fontSize: '11px', fontWeight: '900', cursor: 'pointer', transition: '0.2s' }}
                                            >
                                                COPIAR
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                    
                            {/* 2. SEÇÃO DO CARTÃO FIDELIDADE ATUAL */}
                            <h4 style={{ fontWeight: '900', color: '#0f172a', marginBottom: '20px' }}>Cartão Fidelidade</h4>
                            
                            {fidelidade?.config?.ativo ? (
                                <div style={{ marginBottom: '40px' }}>
                                    <div className="fidelidade-card-scene" onClick={() => setFlipped(!flipped)} style={{ width: '100%', maxWidth: '380px', margin: '0 auto', cursor: 'pointer' }}>
                                        <div className={`fidelidade-card-inner ${flipped ? 'flipped' : ''}`}>
                                            
                                            {/* FRENTE DO CARTÃO */}
                                            <div className="fidelidade-face fidelidade-front" style={{ background: fidelidade.config.cor_cartao || corPrimaria, borderRadius: '24px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                    <div className="fidelidade-chip"></div>
                                                    <Sparkles size={24} color="#fff" opacity={0.6} />
                                                </div>
                                                <div style={{ marginTop: '20px' }}>
                                                    <span style={{ fontSize: '10px', fontWeight: '800', opacity: 0.9 }}>PRÊMIO AO COMPLETAR:</span>
                                                    <h3 style={{ margin: '5px 0', fontSize: '20px', fontWeight: '900', letterSpacing: '-0.5px' }}>{fidelidade.config.premio}</h3>
                                                </div>
                                                <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                                                    <div>
                                                        <div style={{ fontSize: '10px', opacity: 0.9 }}>Progresso Atual</div>
                                                        <div style={{ fontWeight: '900', fontSize: '16px' }}>
                                                            {fidelidade.progresso?.carimbos_atuais || 0} / {fidelidade.config.qtd_objetivo} SELOS
                                                        </div>
                                                    </div>
                                                    <div style={{ textAlign: 'right' }}>
                                                        <RotateCw size={20} style={{ opacity: 0.8 }} />
                                                        <div style={{ fontSize: '8px', fontWeight: 'bold', marginTop: '4px' }}>VER VERSO</div>
                                                    </div>
                                                </div>
                                            </div>
                    
                                            {/* VERSO DO CARTÃO (SELOS) */}
                                            <div className="fidelidade-face fidelidade-back" style={{ background: '#fff', borderRadius: '24px', border: '2px solid #f1f5f9' }}>
                                                <div className="fidelidade-grid-carimbos" style={{ padding: '2px', display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px' }}>
                                                    {Array.from({ length: Number(fidelidade.config.qtd_objetivo) || 10 }).map((_, i) => (
                                                        <div key={i} style={{ 
                                                            width: '100%', aspectRatio: '1/1', borderRadius: '12px', 
                                                            background: i < (fidelidade.progresso?.carimbos_atuais || 0) ? `${corPrimaria}20` : '#f8fafc',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            border: i < (fidelidade.progresso?.carimbos_atuais || 0) ? `2px solid ${corPrimaria}` : '2px dashed #e2e8f0',
                                                            transition: 'all 0.3s ease',
                                                            transform: i < (fidelidade.progresso?.carimbos_atuais || 0) ? 'scale(1)' : 'scale(0.95)'
                                                        }}>
                                                            {i < (fidelidade.progresso?.carimbos_atuais || 0) ? (
                                                                <CheckCircle2 size={24} color={corPrimaria} />
                                                            ) : (
                                                                <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#cbd5e1' }}>{i + 1}</span>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                                <div style={{ textAlign: 'center', padding: '10px', borderTop: '1px solid #f1f5f9' }}>
                                                    <span style={{ fontSize: '11px', fontWeight: '800', color: (fidelidade.progresso?.carimbos_atuais || 0) >= fidelidade.config.qtd_objetivo ? '#16a34a' : '#64748b' }}>
                                                        {(fidelidade.progresso?.carimbos_atuais || 0) >= fidelidade.config.qtd_objetivo 
                                                            ? "🎉 CARTÃO COMPLETADO!" 
                                                            : `FALTAM ${fidelidade.config.qtd_objetivo - (fidelidade.progresso?.carimbos_atuais || 0)} PEDIDOS PARA O PRÊMIO`}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '40px 20px', background: '#fff', borderRadius: '24px', marginBottom: '30px', border: '1px dashed #cbd5e1' }}>
                                    <Trophy size={40} color="#cbd5e1" style={{ marginBottom: '12px', opacity: 0.5 }} />
                                    <h5 style={{ margin: '0 0 5px', color: '#1e293b', fontWeight: '800' }}>Sem Cartão Ativo</h5>
                                    <p style={{ color: '#94a3b8', fontSize: '13px', margin: 0 }}>Fique de olho! Em breve teremos novidades para você.</p>
                                </div>
                            )}
                    
                            {/* 3. SEÇÃO DE CUPONS GERAIS */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                <h4 style={{ fontWeight: '900', color: '#0f172a', margin: 0 }}>Cupons Disponíveis</h4>
                                <div style={{ background: `${corPrimaria}15`, color: corPrimaria, padding: '4px 10px', borderRadius: '10px', fontSize: '10px', fontWeight: '900' }}>
                                    {cuponsExclusivos.length} DISPONÍVEIS
                                </div>
                            </div>
                    
                            {cuponsExclusivos.length === 0 ? (
                                <div style={{ padding: '30px', background: '#fff', borderRadius: '24px', textAlign: 'center', border: '1px dashed #e2e8f0' }}>
                                    <Ticket size={32} color="#cbd5e1" style={{ marginBottom: '10px', opacity: 0.5 }} />
                                    <p style={{ color: '#94a3b8', fontSize: '13px', margin: 0 }}>Você não possui cupons no momento.</p>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    {cuponsExclusivos.map(cupom => (
                                        <div key={cupom.id} style={{ 
                                            background: '#fff', borderRadius: '20px', padding: '18px',
                                            border: `2px dashed ${corPrimaria}40`, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                            position: 'relative', overflow: 'hidden'
                                        }}>
                                            <div style={{ paddingLeft: '10px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                                    <Zap size={14} color={corPrimaria} fill={corPrimaria} />
                                                    <span style={{ fontSize: '11px', fontWeight: '900', color: corPrimaria, letterSpacing: '0.5px' }}>CÓDIGO: {cupom.codigo}</span>
                                                </div>
                                                <h5 style={{ margin: 0, fontSize: '17px', fontWeight: '900', color: '#0f172a' }}>
                                                    {cupom.tipo === 'porcentagem' ? `${cupom.valor}% OFF` : `R$ ${parseFloat(cupom.valor).toFixed(2).replace('.', ',')} de desconto`}
                                                </h5>
                                                <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#64748b', fontWeight: '600' }}>
                                                    {cupom.descricao || 'Válido por tempo limitado'}
                                                </p>
                                            </div>
                    
                                            <button 
                                                onClick={() => { 
                                                    navigator.clipboard.writeText(cupom.codigo); 
                                                    toast.success("Cupom copiado!"); 
                                                }}
                                                style={{ background: corPrimaria, color: '#fff', border: 'none', padding: '12px 20px', borderRadius: '14px', fontWeight: '900', fontSize: '12px', cursor: 'pointer', boxShadow: `0 4px 12px ${corPrimaria}40` }}
                                            >
                                                COPIAR
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ABA: PERFIL */}
                    {tabAtiva === 'perfil' && (
                        <div style={{ animation: 'fadeIn 0.3s ease' }}>
                            <div style={{ background: '#fff', borderRadius: '24px', padding: '25px', boxShadow: '0 4px 15px rgba(0,0,0,0.03)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
                                    <h4 style={{ fontWeight: '900', margin: 0 }}>Meus Dados</h4>
                                    <button onClick={() => editando ? handleUpdatePerfil() : setEditando(true)} style={{ background: editando ? corPrimaria : '#f1f5f9', border: 'none', padding: '10px 18px', borderRadius: '12px', color: editando ? '#fff' : '#0f172a', fontWeight: '900', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                        {editando ? <><Save size={16}/> Salvar</> : <><Edit3 size={16}/> Editar</>}
                                    </button>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                    {[
                                        { label: 'NOME COMPLETO', key: 'nome', icon: <User size={16}/> },
                                        { label: 'WHATSAPP / CELULAR', key: 'whatsapp', icon: <Smartphone size={16}/> },
                                        { label: 'CPF', key: 'cpf', icon: <FileText size={16}/> },
                                        { label: 'ENDEREÇO DE ENTREGA', key: 'endereco', icon: <MapPin size={16}/> }
                                    ].map(field => (
                                        <div key={field.key}>
                                            <label style={{ fontSize: '10px', fontWeight: '900', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                                                {field.icon} {field.label}
                                            </label>
                                            <input 
                                                disabled={!editando} 
                                                value={formData[field.key] || ''} 
                                                onChange={e => setFormData({...formData, [field.key]: e.target.value})} 
                                                style={{ width: '100%', padding: '12px 15px', borderRadius: '12px', border: '1.5px solid #f1f5f9', background: editando ? '#fff' : '#f8fafc', fontWeight: '700', color: '#0f172a', outline: 'none', transition: 'all 0.2s', borderColor: editando ? corPrimaria : '#f1f5f9' }} 
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </main>

            {/* TAB BAR INFERIOR COM 4 ABAS (PEDIDOS, PAGAMENTOS, BENEFÍCIOS, PERFIL) */}
            <nav style={{ position: 'fixed', bottom: '15px', left: '15px', right: '15px', background: '#0f172a', height: '70px', borderRadius: '24px', display: 'flex', justifyContent: 'space-around', alignItems: 'center', boxShadow: '0 10px 30px rgba(0,0,0,0.3)', zIndex: 1000 }}>
                <button onClick={() => setTabAtiva('pedidos')} style={{ background: 'none', border: 'none', color: tabAtiva === 'pedidos' ? corPrimaria : '#64748b', cursor: 'pointer', flex: 1 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                        <Package size={22} strokeWidth={tabAtiva === 'pedidos' ? 3 : 2} />
                        <span style={{ fontSize: '9px', fontWeight: '900' }}>PEDIDOS</span>
                    </div>
                </button>
                <button onClick={() => setTabAtiva('pagamentos')} style={{ background: 'none', border: 'none', color: tabAtiva === 'pagamentos' ? corPrimaria : '#64748b', cursor: 'pointer', flex: 1 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                        <QrCode size={22} strokeWidth={tabAtiva === 'pagamentos' ? 3 : 2} />
                        <span style={{ fontSize: '9px', fontWeight: '900' }}>PAGAMENTOS</span>
                    </div>
                </button>
                <button onClick={() => setTabAtiva('cupons')} style={{ background: 'none', border: 'none', color: tabAtiva === 'cupons' ? corPrimaria : '#64748b', cursor: 'pointer', flex: 1 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                        <Zap size={22} strokeWidth={tabAtiva === 'cupons' ? 3 : 2} />
                        <span style={{ fontSize: '9px', fontWeight: '900' }}>BENEFÍCIOS</span>
                    </div>
                </button>
                <button onClick={() => setTabAtiva('perfil')} style={{ background: 'none', border: 'none', color: tabAtiva === 'perfil' ? corPrimaria : '#64748b', cursor: 'pointer', flex: 1 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                        <User size={22} strokeWidth={tabAtiva === 'perfil' ? 3 : 2} />
                        <span style={{ fontSize: '9px', fontWeight: '900' }}>PERFIL</span>
                    </div>
                </button>
            </nav>

             {/* MODAL DETALHES COM CUPOM FISCAL COMPLETO */}
            {pedidoSelecionado && (
                <div className="modal-cupom-overlay no-print">
                    <div className="modal-cupom-content">
                        <div className="no-print" style={{ padding: '15px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff' }}>
                            <button onClick={() => setPedidoSelecionado(null)} className="btn-acao-cupom" style={{ color: '#ef4444', border: 'none', background: 'none', cursor: 'pointer' }}><X size={24}/></button>
                            <div style={{ display: 'flex', gap: '15px' }}>
                                <button onClick={compartilharPedido} className="btn-acao-cupom" title="Compartilhar" style={{ border: 'none', background: 'none', cursor: 'pointer' }}><Share2 size={22} color="#64748b"/></button>
                                <button onClick={imprimirCupom} className="btn-acao-cupom" title="Imprimir" style={{ border: 'none', background: 'none', cursor: 'pointer' }}><Printer size={22} color={corPrimaria}/></button>
                            </div>
                        </div>

                        {/* CONTEÚDO DO CUPOM FISCAL PARA EXIBIÇÃO E IMPRESSÃO */}
                        <div id="cupom-fiscal">
                            <div className="cupom-header">
                                <ReceiptText size={32} style={{ marginBottom: '8px' }} />
                                <h3>{pedidoSelecionado.loja_nome || 'PEDIDO REALIZADO'}</h3>
                                <small>CUPOM INFORMATIVO (NÃO FISCAL)</small>
                            </div>
                            <div className="cupom-divider"></div>
                            <div style={{ fontSize: '12px', marginBottom: '10px' }}>
                                <div><b>PEDIDO:</b> #{pedidoSelecionado.id}</div>
                                <div><b>DATA:</b> {new Date(pedidoSelecionado.criado_em || Date.now()).toLocaleString('pt-BR')}</div>
                                <div><b>CLIENTE:</b> {pedidoSelecionado.cliente_nome_cadastro || clienteLogado.nome}</div>
                                <div><b>PAGAMENTO:</b> {pedidoSelecionado.tipo_pagamento || 'PIX'}</div>
                                {pedidoSelecionado.tipo_entrega && <div><b>ENTREGA:</b> {pedidoSelecionado.tipo_entrega.toUpperCase()}</div>}
                            </div>
                            <div className="cupom-divider"></div>
                            <div className="cupom-body">
                                <div className="cupom-item-row" style={{ fontWeight: 'bold', fontSize: '11px', marginBottom: '8px' }}>
                                    <span>QTD | DESCRIÇÃO</span>
                                    <span>TOTAL</span>
                                </div>
                                {pedidoSelecionado.itens && pedidoSelecionado.itens.map((item, idx) => (
                                    <div key={idx} style={{ marginBottom: '8px' }}>
                                        <div className="cupom-item-row">
                                            <span>{item.quantidade}x {item.nome}</span>
                                            <span>R$ {(item.quantidade * (item.preco_unitario || item.preco || 0)).toFixed(2).replace('.', ',')}</span>
                                        </div>
                                        {item.escolhas && <div className="cupom-sub-item">{item.escolhas}</div>}
                                    </div>
                                ))}
                            </div>
                            <div className="cupom-divider"></div>
                            <div className="cupom-footer">
                                <div className="cupom-item-row">
                                    <span>SUBTOTAL:</span>
                                    <span>R$ {parseFloat(pedidoSelecionado.total || 0).toFixed(2).replace('.', ',')}</span>
                                </div>
                                {pedidoSelecionado.taxa_entrega > 0 && (
                                    <div className="cupom-item-row">
                                        <span>TAXA ENTREGA:</span>
                                        <span>R$ {parseFloat(pedidoSelecionado.taxa_entrega).toFixed(2).replace('.', ',')}</span>
                                    </div>
                                )}
                                <div className="cupom-total-row">
                                    <span>TOTAL:</span>
                                    <span>R$ {parseFloat(pedidoSelecionado.total || 0).toFixed(2).replace('.', ',')}</span>
                                </div>
                            </div>
                            <div className="cupom-divider"></div>
                            <div style={{ marginTop: '10px', fontSize: '11px', color: '#000', textAlign: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', marginBottom: '4px' }}>
                                    <MapPin size={12}/> {pedidoSelecionado.endereco_entrega || pedidoSelecionado.endereco_formatado || 'RETIRADA NO ESTABELECIMENTO'}
                                </div>
                                <div style={{ fontWeight: 'bold', marginTop: '15px', letterSpacing: '1px' }}>OBRIGADO PELA PREFERÊNCIA!</div>
                                <div style={{ fontSize: '9px', marginTop: '5px', opacity: 0.6 }}>Universo Mídia Digital</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}